import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { 
  ArrowLeft, Play, Pause, SkipBack, SkipForward, 
  Volume2, VolumeX, Volume1, Loader2, User, Calendar, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoResponseViewerPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Video state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const voiceOverAudioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  // Load project
  useEffect(() => {
    const loadProject = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/video-studio/view/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProject(response.data);
      } catch (err) {
        console.error('Failed to load video:', err);
        if (err.response?.status === 403) {
          setError('You do not have permission to view this video.');
        } else if (err.response?.status === 404) {
          setError('Video not found.');
        } else {
          setError('Failed to load video response.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadProject();
  }, [projectId, navigate]);

  // Handle video metadata load
  const handleVideoLoad = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setVideoReady(true);
    }
  };

  // Update video time and keep voice-over in sync
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const videoTime = videoRef.current.currentTime;
      setCurrentTime(videoTime);
      
      // Keep voice-over in sync during playback
      if (isPlaying && voiceOverAudioRef.current && project?.voice_over_url) {
        const voiceOverStartTime = project?.voice_over_start_time || 0;
        if (videoTime >= voiceOverStartTime) {
          const expectedAudioTime = videoTime - voiceOverStartTime;
          const actualAudioTime = voiceOverAudioRef.current.currentTime;
          
          // Re-sync if drift is more than 0.3 seconds
          if (Math.abs(expectedAudioTime - actualAudioTime) > 0.3) {
            voiceOverAudioRef.current.currentTime = expectedAudioTime;
          }
          
          // Ensure audio is playing when video is playing
          if (voiceOverAudioRef.current.paused) {
            voiceOverAudioRef.current.play().catch(() => {});
          }
        }
      }
    }
  };

  // Play/Pause toggle - syncs video and voice-over
  const togglePlay = () => {
    if (videoRef.current && videoReady) {
      if (isPlaying) {
        videoRef.current.pause();
        // Pause voice-over
        if (voiceOverAudioRef.current) {
          voiceOverAudioRef.current.pause();
        }
      } else {
        videoRef.current.play().catch(err => {
          console.error('Play error:', err);
          toast.error('Unable to play video');
        });
        // Play voice-over in sync
        if (voiceOverAudioRef.current && project?.voice_over_url) {
          const voiceOverStartTime = project?.voice_over_start_time || 0;
          const videoTime = videoRef.current.currentTime;
          console.log('Voice-over sync:', { voiceOverStartTime, videoTime, url: project.voice_over_url });
          if (videoTime >= voiceOverStartTime) {
            voiceOverAudioRef.current.currentTime = videoTime - voiceOverStartTime;
            voiceOverAudioRef.current.play().catch(err => {
              console.error('Voice-over play error:', err);
              // Don't show error toast for autoplay restrictions - the audio will play on next user interaction
            });
          }
        }
      }
    }
  };

  // Toggle mute - also affects voice-over
  const toggleMute = () => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.muted = false;
        videoRef.current.volume = volume;
        if (voiceOverAudioRef.current) {
          voiceOverAudioRef.current.muted = false;
          voiceOverAudioRef.current.volume = volume;
        }
        setIsMuted(false);
      } else {
        videoRef.current.muted = true;
        if (voiceOverAudioRef.current) {
          voiceOverAudioRef.current.muted = true;
        }
        setIsMuted(true);
      }
    }
  };

  // Change volume - also affects voice-over
  const handleVolumeChange = (newVolume) => {
    if (videoRef.current) {
      const vol = newVolume[0];
      videoRef.current.volume = vol;
      if (voiceOverAudioRef.current) {
        voiceOverAudioRef.current.volume = vol;
      }
      setVolume(vol);
      if (vol === 0) {
        setIsMuted(true);
        videoRef.current.muted = true;
        if (voiceOverAudioRef.current) voiceOverAudioRef.current.muted = true;
      } else if (isMuted) {
        setIsMuted(false);
        videoRef.current.muted = false;
        if (voiceOverAudioRef.current) voiceOverAudioRef.current.muted = false;
      }
    }
  };

  // Seek video - also sync voice-over
  const seekTo = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
      // Sync voice-over position
      if (voiceOverAudioRef.current && project?.voice_over_url) {
        const voiceOverStartTime = project?.voice_over_start_time || 0;
        if (time >= voiceOverStartTime) {
          voiceOverAudioRef.current.currentTime = time - voiceOverStartTime;
        } else {
          voiceOverAudioRef.current.currentTime = 0;
          voiceOverAudioRef.current.pause();
        }
      }
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Draw annotations on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !project?.annotations) return;
    
    const ctx = canvas.getContext('2d');
    
    const drawAnnotations = () => {
      // Match canvas size to video
      const rect = video.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw annotations visible at current time
      const visibleAnnotations = project.annotations.filter(a => 
        currentTime >= a.timestamp && currentTime <= a.timestamp + (a.duration || 3)
      );
      
      visibleAnnotations.forEach(annotation => {
        ctx.strokeStyle = annotation.color || '#ef4444';
        ctx.fillStyle = annotation.color || '#ef4444';
        ctx.lineWidth = annotation.lineWidth || 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        const points = annotation.points || [];
        
        switch (annotation.type) {
          case 'line':
            if (points.length >= 2) {
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
              ctx.stroke();
            }
            break;
            
          case 'arrow':
            if (points.length >= 2) {
              const start = points[0];
              const end = points[points.length - 1];
              ctx.beginPath();
              ctx.moveTo(start.x, start.y);
              ctx.lineTo(end.x, end.y);
              ctx.stroke();
              
              const angle = Math.atan2(end.y - start.y, end.x - start.x);
              const headLength = 15;
              ctx.beginPath();
              ctx.moveTo(end.x, end.y);
              ctx.lineTo(end.x - headLength * Math.cos(angle - Math.PI / 6), end.y - headLength * Math.sin(angle - Math.PI / 6));
              ctx.moveTo(end.x, end.y);
              ctx.lineTo(end.x - headLength * Math.cos(angle + Math.PI / 6), end.y - headLength * Math.sin(angle + Math.PI / 6));
              ctx.stroke();
            }
            break;
            
          case 'circle':
            if (points.length >= 2) {
              const start = points[0];
              const end = points[points.length - 1];
              const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
              ctx.beginPath();
              ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
              ctx.stroke();
            }
            break;
            
          case 'freehand':
            if (points.length >= 2) {
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              points.forEach(point => ctx.lineTo(point.x, point.y));
              ctx.stroke();
            }
            break;
            
          case 'text':
            if (annotation.text && annotation.position) {
              ctx.font = 'bold 20px Inter, sans-serif';
              ctx.fillText(annotation.text, annotation.position.x, annotation.position.y);
            }
            break;
            
          default:
            break;
        }
      });
    };
    
    drawAnnotations();
  }, [project, currentTime]);

  // Update canvas size on window resize
  useEffect(() => {
    const updateCanvasSize = () => {
      if (videoRef.current && canvasRef.current) {
        const rect = videoRef.current.getBoundingClientRect();
        canvasRef.current.width = rect.width;
        canvasRef.current.height = rect.height;
      }
    };
    
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [videoReady]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8" data-testid="video-response-viewer">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <Badge className="bg-secondary">Coach Response</Badge>
          </div>
          <h1 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tight">
            {project.title || 'Video Response'}
          </h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {project.coach_name}
            </span>
            {project.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(project.published_at)}
              </span>
            )}
          </div>
        </div>

        {/* Video Player */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {/* Video Container */}
            <div className="relative bg-black" style={{ aspectRatio: '16/9' }}>
              <video
                ref={videoRef}
                src={project.source_video_url}
                className="w-full h-full object-contain"
                onLoadedMetadata={handleVideoLoad}
                onTimeUpdate={handleTimeUpdate}
                onPlay={() => setIsPlaying(true)}
                onPause={() => {
                  setIsPlaying(false);
                  // Pause voice-over when video pauses
                  if (voiceOverAudioRef.current) {
                    voiceOverAudioRef.current.pause();
                  }
                }}
                onError={() => toast.error('Unable to load video')}
                playsInline
                crossOrigin="anonymous"
                data-testid="response-video-player"
              />
              
              {/* Hidden audio element for voice-over sync playback */}
              {project.voice_over_url && (
                <audio
                  ref={voiceOverAudioRef}
                  src={project.voice_over_url}
                  preload="auto"
                  style={{ display: 'none' }}
                  onCanPlay={() => console.log('Voice-over audio loaded and ready to play')}
                  onError={(e) => console.error('Voice-over audio error:', e.target.error)}
                />
              )}
              
              {/* Annotations Canvas Overlay */}
              <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
              />
              
              {/* Play button overlay when paused */}
              {!isPlaying && videoReady && (
                <button 
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                >
                  <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center">
                    <Play className="w-8 h-8 text-primary-foreground ml-1" />
                  </div>
                </button>
              )}
            </div>
            
            {/* Video Controls */}
            <div className="p-4 bg-card space-y-3">
              {/* Progress Bar */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-12 text-right font-mono">
                  {formatTime(currentTime)}
                </span>
                <Slider
                  value={[currentTime]}
                  max={duration || 100}
                  step={0.1}
                  onValueChange={([value]) => seekTo(value)}
                  className="flex-1"
                  data-testid="video-progress-slider"
                />
                <span className="text-xs text-muted-foreground w-12 font-mono">
                  {formatTime(duration)}
                </span>
              </div>
              
              {/* Playback Controls */}
              <div className="flex items-center justify-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => seekTo(Math.max(0, currentTime - 10))}
                  className="h-10 w-10"
                >
                  <SkipBack className="w-5 h-5" />
                </Button>
                <Button 
                  onClick={togglePlay} 
                  disabled={!videoReady}
                  size="icon"
                  className="h-14 w-14 rounded-full"
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => seekTo(Math.min(duration, currentTime + 10))}
                  className="h-10 w-10"
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
                
                {/* Volume Control */}
                <div 
                  className="relative ml-4 flex items-center"
                  onMouseEnter={() => setShowVolumeSlider(true)}
                  onMouseLeave={() => setShowVolumeSlider(false)}
                >
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={toggleMute}
                    className="h-10 w-10"
                    data-testid="volume-btn"
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX className="w-5 h-5" />
                    ) : volume < 0.5 ? (
                      <Volume1 className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </Button>
                  
                  {/* Volume Slider - shows on hover */}
                  <div className={`absolute left-full ml-2 flex items-center bg-card border rounded-lg px-3 py-2 shadow-lg transition-opacity ${showVolumeSlider ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                    <Slider
                      value={[isMuted ? 0 : volume]}
                      max={1}
                      step={0.05}
                      onValueChange={handleVolumeChange}
                      className="w-24"
                      data-testid="volume-slider"
                    />
                    <span className="text-xs text-muted-foreground ml-2 w-8">
                      {Math.round((isMuted ? 0 : volume) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Voice-over indicator */}
              {project.voice_over_url && (
                <div className="flex items-center justify-center gap-2 text-xs text-green-600 bg-green-500/10 rounded-lg px-3 py-2">
                  <Volume2 className="w-4 h-4" />
                  <span>Coach voice-over included - plays automatically with video</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Annotations Info */}
        {project.annotations && project.annotations.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase">
                Coach Annotations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Your coach has added {project.annotations.length} annotation{project.annotations.length !== 1 ? 's' : ''} to highlight key moments.
                Watch the video to see them appear at the relevant times.
              </p>
              <div className="flex flex-wrap gap-2">
                {project.annotations.map((annotation, index) => (
                  <Button
                    key={annotation.id || index}
                    variant="outline"
                    size="sm"
                    onClick={() => seekTo(annotation.timestamp)}
                    className="text-xs"
                  >
                    <div 
                      className="w-2 h-2 rounded-full mr-2" 
                      style={{ backgroundColor: annotation.color || '#ef4444' }}
                    />
                    {formatTime(annotation.timestamp)}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Voice-over Info - simplified since it now plays with video */}
        {project.voice_over_url && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
                <Volume2 className="w-5 h-5" />
                Voice Commentary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Your coach has recorded voice commentary that plays automatically with the video.
                {project.voice_over_start_time > 0 && (
                  <span className="block mt-1">
                    Commentary starts at {formatTime(project.voice_over_start_time)}.
                  </span>
                )}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default VideoResponseViewerPage;
